import { myLocalStorage } from "../../StorageHelper";
import { getAWSAllIAMRoles, getAWSAllOIDC, getAWSAllSecrets } from "./Api/awsservicerapi";
import { fetchAzureK8ServiceAccounts, fetchAzureK8ServiceSecrets, fetchAzureK8Tokens, getAzureAllActivity, getAzureAllIdentity, getAzureAllRoles, fetchAzureKeyVaultSecrets } from "./Api/azureserviceapi";
import { fetchGCPApiTokens, fetchGCPCloudSecrets, fetchGCPK8ServiceAccounts, fetchGCPK8ServiceSecrets, getGCPAllActivity, getGCPAllIdentity, getGCPAllRoles } from "./Api/gcpserviceapi";
import { ApiToken, GCPKubernetesSecret, GCPKubernetesServiceAccount, ServiceAccount } from "./interface";

const formatProtobufDate = (value: any) => {
    if (!value) return "N/A";
    if (typeof value === "string" && value.includes("seconds:")) {
        try {
            const seconds = parseInt(value.match(/seconds:\s*(\d+)/)?.[1] || "0", 10);
            const nanos = parseInt(value.match(/nanos:\s*(\d+)/)?.[1] || "0", 10);
            return new Date(seconds * 1000 + nanos / 1e6).toLocaleString("en-GB");
        } catch {
            return value;
        }
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("en-GB");
};


const fetchGCPServiceAccounts = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const [roles, identity, activity] = await Promise.all([
        getGCPAllRoles(latestTenant),
        getGCPAllIdentity(latestTenant),
        getGCPAllActivity(latestTenant)
    ]);
    return { roles, identity, activity }
}

const fetchAzureServiceAccounts = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const [roles, identity, activity] = await Promise.all([
        getAzureAllRoles(latestTenant),
        getAzureAllIdentity(latestTenant),
        getAzureAllActivity(latestTenant)
    ]);
    return { roles, identity, activity }
}

export const getGCPCloudServiceAccounts = async (): Promise<ServiceAccount[]> => {
    const { roles, identity, activity } = await fetchGCPServiceAccounts();

    const result: ServiceAccount[] = [];

    Object.entries(identity || {}).forEach(([projectId, serviceAccounts]) => {
        (serviceAccounts as any[]).forEach((account, index) => {
            const email = account.serviceAccountEmail;

            // Merge roles from both APIs
            const rolesFromRolesAPI = roles[email]?.map((r: any) => r.roleName) || [];
            const rolesCombined =
                rolesFromRolesAPI.length > 0
                    ? rolesFromRolesAPI
                    : account.roles || [];

            // Determine managed type
            const managedType = account.isUserManaged ? "User Managed" : "Google Managed";

            // Activity value
            const activityCount =
                activity[email] !== undefined ? activity[email] : account.activity || 0;

            // Formatted validity period
            const validityPeriod = account.syncedAt
                ? new Date(account.syncedAt).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
                : "-";

            // Push final mapped object
            result.push({
                id: `gcp-sa-${index + 1}`,
                email,
                keyId: account.keyId || "-",
                algorithm: account.algorithm || "KEY_ALG_RSA_2048",
                validityPeriod,
                managedType,
                activity: activityCount,
                serviceroles: rolesCombined,
                serviceDetails: account,
                clusterName: account.clusterId,
                // Risk & alert fields
                riskChangeCategory: account.riskChangeCategory ?? null,
                alertCount: account.alertCount ?? 0,
                alertCountDelta: account.alertCountDelta ?? 0,
                criticalAlertCount: account.criticalAlertCount ?? 0,
                criticalAlertCountDelta: account.criticalAlertCountDelta ?? 0,
                highAlertCount: account.highAlertCount ?? 0,
                highAlertCountDelta: account.highAlertCountDelta ?? 0,
                highPrivilegeRoleCount: account.highPrivilegeRoleCount ?? 0,
                highPrivilegeRoleCountDelta: account.highPrivilegeRoleCountDelta ?? 0,
                riskTags: account.riskTags ?? [],
                tagInfo: account.tagInfo ?? {},
                riskComparedAt: account.riskComparedAt ?? "",
                isDormant: account.isDormant ?? false,
                lastActivityMessage: account.lastActivityMessage || "-",
                owners: account.owners ?? [],
                previousOwners: account.previousOwners ?? [],
                frequentUsers: account.frequentUsers ?? [],
            });
        });
    });

    return result;
};


export const getGCPCloudTokens = async (): Promise<ApiToken[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchGCPApiTokens(latestTenant);

    const tokens = Array.isArray(response) ? response : [response];

    return tokens.map((token) => ({
        id: `gcp-token-${token.id}`,
        name: token.serviceAccountEmail || "Unknown Token",
        type: token.isUserManaged ? "Service Account Key" : "Managed Key",
        keyId: token.keyId || "N/A",
        cluster: token.projectId || "N/A",
        namespace: token.tenantName || "N/A",
        createdAt: new Date(token.validAfter).toLocaleString(),
        expiresAt: new Date(token.validBefore).toLocaleString(),
        lastUsed: "N/A",
        activity: token.activity ?? 0,
        tokenDetails: token
    }));
};


export const getGCPCloudSecrets = async () => {

    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchGCPCloudSecrets(latestTenant);

    const detailResponse = response.map((item: any) => ({
        id: `gcp-secret-${item.id}`,
        name: item.displayName || item.name?.split("/").pop() || "Unknown Secret",
        fullName: item.name || "N/A",
        displayName: item.displayName || item.name?.split("/").pop() || "Unknown Secret",
        type: "Secret Manager",
        replicationType: item.replicationType || "N/A",
        keyNames: item.keyNames || [],
        keys: item.keyNames?.length || 0,
        projectId: item.projectId || "N/A",
        tenantName: item.tenantName || "N/A",
        owners: Array.isArray(item.owners) ? item.owners : [],
        frequentUsers: Array.isArray(item.frequentUsers) ? item.frequentUsers : [],
        owner: (Array.isArray(item.owners) && item.owners.length > 0 ? item.owners[0] : null) || item.owner || null,
        rotationEnabled: item.rotationEnabled ? "Enabled" : "Disabled",
        isManagedSecret: item.isAdminManaged
            ? "Admin Managed Secret"
            : item.isManagedSecret ? "Managed Secret" : "User Managed Secret",
        isAdminManaged: item.isAdminManaged === true,
        createdAt: formatProtobufDate(item.createTime),
        lastModified: formatProtobufDate(item.updateTime || item.createTime),
        lastActivity: item.lastActivity
            ? formatProtobufDate(item.lastActivity)
            : "No activity in last 30 days",
        activity: item.activity ?? 0,
        secretDetails: item,
        cloudSecretDetails: item
    }));

    return detailResponse;


}


export const getAWSCloudIAMRole = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

    let response = await getAWSAllIAMRoles(latestTenant, selectedAwsAccount);

    // Map API response to required format
    const formattedRoles = response.map((item: any, index) => ({
        id: `aws-role-${item.id ?? index}`,
        email: item.roleArn, // using roleArn as identifier
        keyId: item.accountId, // no key ID in response, using accountId for now
        algorithm: "RSA-2048", // static placeholder
        validityPeriod: item.createDate
            ? `${new Date(item.createDate).toLocaleString()} → N/A`
            : "N/A",
        managedType: "IAM Role", // derived from API
        activity: item.activity ?? 0,
        roles: [
            {
                roleName: item.roleName,
                description: item.description,
                region: item.region,
                path: item.path,
            },
        ],
        serviceDetails: item,
        // Risk & alert fields
        riskChangeCategory: item.riskChangeCategory ?? null,
        alertCount: item.alertCount ?? 0,
        alertCountDelta: item.alertCountDelta ?? 0,
        criticalAlertCount: item.criticalAlertCount ?? 0,
        criticalAlertCountDelta: item.criticalAlertCountDelta ?? 0,
        highAlertCount: item.highAlertCount ?? 0,
        highAlertCountDelta: item.highAlertCountDelta ?? 0,
        highPrivilegeRoleCount: item.highPrivilegePolicyCount ?? item.highPrivilegeRoleCount ?? 0,
        highPrivilegeRoleCountDelta: item.highPrivilegePolicyCountDelta ?? item.highPrivilegeRoleCountDelta ?? 0,
        riskTags: item.riskTags ?? [],
        tagInfo: item.tagInfo ?? {},
        riskComparedAt: item.riskComparedAt ?? "",
        isDormant: item.isDormant ?? false,
        lastActivityMessage: item.lastActivityMessage || "-",
        owners: item.owners ?? [],
        previousOwners: item.previousOwners ?? [],
        frequentUsers: item.frequentUsers ?? [],
    }));

    return formattedRoles;
};


export const getAWSCloudOIDC = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

    let response = await getAWSAllOIDC(latestTenant, selectedAwsAccount);

    const formattedData = response.map((item: any, index: any) => {
        const createdAt = new Date(item.createDate);
        const expiresAt = new Date(createdAt);
        expiresAt.setFullYear(createdAt.getFullYear() + 1);

        return {
            id: `aws-token-${item.id ?? index + 1}`,
            name: item.url || "unknown-oidc-provider",
            type: "OIDC Provider",
            keyId: item.accountId ?? "N/A",
            cluster: "N/A",
            namespace: "N/A",
            createdAt: createdAt.toLocaleString("en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }),
            expiresAt: expiresAt.toLocaleString("en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }),
            lastUsed: item.lastUsedAt
                ? new Date(item.lastUsedAt).toLocaleString("en-US", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
                : "N/A",
            activity: item.activity ?? 0,
            oidcDetails: item
        };
    });

    return formattedData;
};


export const getAWSCloudSecrets = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

    let response = await getAWSAllSecrets(latestTenant, selectedAwsAccount);

    const formattedSecrets = response.map((item: any, index: any) => {
        // Prepare date fields
        const createdAt = new Date(item.createdAt);
        const lastModified = item.lastChangedAt
            ? new Date(item.lastChangedAt)
            : createdAt;

        // Estimate number of keys (if tags or structure is available)
        const tagKeys = item.tags ? Object.keys(item.tags) : [];
        const keyNames = tagKeys.length ? tagKeys : ["SECRET_VALUE"];
        const keyCount = keyNames.length;

        return {
            id: `aws-secret-${item.id ?? index + 1}`,
            name: item.name || "Unnamed Secret",
            type: "Secrets Manager",
            fullName: item.arn || item.name || "N/A",
            namespace: "N/A",
            cluster: "N/A",
            keys: keyCount,
            keyNames,
            owners: Array.isArray(item.owners) ? item.owners : [],
            frequentUsers: Array.isArray(item.frequentUsers) ? item.frequentUsers : [],
            owner: (Array.isArray(item.owners) && item.owners.length > 0 ? item.owners[0] : null) || item.owner || null,
            createdAt: createdAt.toLocaleString("en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }),
            lastModified: lastModified.toLocaleString("en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }),
            lastActivity: item.lastActivityAt
                ? new Date(item.lastActivityAt).toLocaleString("en-US", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
                : lastModified.toLocaleString("en-US", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                }),
            activity: item.activity ?? 0,
            secretDetails: item
        };
    });

    return formattedSecrets;
};


// ============================================================================
// AZURE CLOUD DATA
// ============================================================================

export const getAzureCloudServiceAccounts = async (): Promise<ServiceAccount[]> => {
    const { roles, identity, activity } = await fetchAzureServiceAccounts();

    const result: ServiceAccount[] = [];

    Object.entries(identity || {}).forEach(([subscriptionId, identities]) => {
        (identities as any[]).forEach((account, index) => {
            const name = account.name;

            const rolesForIdentity = roles[name]?.map((r: any) => r.roleName) || [];
            const rolesCombined =
                rolesForIdentity.length > 0
                    ? rolesForIdentity
                    : account.roles || [];

            const managedType = account.type?.includes("userAssigned")
                ? "User Managed"
                : "System Managed";

            const validityPeriod = account.syncedAt
                ? new Date(account.syncedAt).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
                : "-";

            result.push({
                id: `azure-identity-${index + 1}`,
                email: account.name || "-",
                keyId: account.instanceId || "-",
                algorithm: "Managed Identity",
                validityPeriod,
                managedType,
                activity: activity?.[account.principalId] ?? account.activity ?? 0,
                serviceroles: rolesCombined,
                serviceDetails: account,
                clusterName: account.clusterId,
                // Risk & alert fields
                riskChangeCategory: account.riskChangeCategory ?? null,
                alertCount: account.alertCount ?? 0,
                alertCountDelta: account.alertCountDelta ?? 0,
                criticalAlertCount: account.criticalAlertCount ?? 0,
                criticalAlertCountDelta: account.criticalAlertCountDelta ?? 0,
                highAlertCount: account.highAlertCount ?? 0,
                highAlertCountDelta: account.highAlertCountDelta ?? 0,
                highPrivilegeRoleCount: account.highPrivilegeRoleCount ?? 0,
                highPrivilegeRoleCountDelta: account.highPrivilegeRoleCountDelta ?? 0,
                riskTags: account.riskTags ?? [],
                tagInfo: account.tagInfo ?? {},
                riskComparedAt: account.riskComparedAt ?? "",
                isDormant: account.isDormant ?? false,
                lastActivityMessage: account.lastActivityMessage || "-",
                owners: account.owners ?? [],
                previousOwners: account.previousOwners ?? [],
                frequentUsers: account.frequentUsers ?? [],
            });
        });
    });

    return result.sort((a, b) => Number(b.activity || 0) - Number(a.activity || 0));
};


export function getAzureCloudTokens() {
    /**
     * {
            id: "azure-token-1",
            name: "azure-ad-token-prod",
            type: "Azure AD Token",
            keyId: "azure-ad-token-xyz789",
            cluster: "N/A",
            namespace: "N/A",
            createdAt: "05/20/2025, 13:30:00",
            expiresAt: "05/20/2026, 13:30:00",
            lastUsed: "07/07/2025, 17:10:25",
            activity: 289,
        }
     */
    return [

    ];
}

export const getAzureCloudSecrets = async (): Promise<any[]> => {

    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchAzureKeyVaultSecrets(latestTenant);

    return (response || []).map((item: any) => ({
        ...item,
        id: item.id,
        name: item.secretName,
        fullName: `${item.vaultName}/${item.secretName}`,
        type: "Key Vault",
        vaultName: item.vaultName,
        createdAt: item.createdDate,
        lastModified: item.updatedDate,
        secretDetails: item,
    }));
}

// ============================================================================
// GCP KUBERNETES DATA
// ============================================================================

export const getGCPKubernetesServiceAccounts = async (): Promise<GCPKubernetesServiceAccount[]> => {

    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchGCPK8ServiceAccounts(latestTenant);
    let detailResponse = response.map((item: any) => ({
        ...item,
        k8ServiceDetails: item
    }))

    return detailResponse;
}

export function getGCPKubernetesTokens() {

    /**
     * {
            id: "gcp-k8s-token-1",
            name: "system:serviceaccount:test-nhi-ns:custom-sa",
            namespace: "test-nhi-ns",
            cluster: "gke-autopilot-cluster-1",
            type: "kubernetes.io/service-account-token",
            syncedAt: "22/09/2025, 21:32:21",
            activity: 234,
        }
     */

    return [
    ];
}

export const getGCPKubernetesSecrets = async (): Promise<GCPKubernetesSecret[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchGCPK8ServiceSecrets(latestTenant);

    let detailResponse = response.map((item: any) => ({
        ...item,
        k8Secrets: item
    }))

    return detailResponse;
}

// ============================================================================
// AWS KUBERNETES DATA
// ============================================================================

export function getAWSKubernetesServiceAccounts() {
    return [];
}

export function getAWSKubernetesTokens() {
    return [];
}

export function getAWSKubernetesSecrets() {
    return [];
}

// ============================================================================
// AZURE KUBERNETES DATA
// ============================================================================

export const getAzureKubernetesServiceAccounts = async (): Promise<any[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchAzureK8ServiceAccounts(latestTenant);

    return response;
}

export const getAzureKubernetesTokens = async (): Promise<any[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchAzureK8Tokens(latestTenant);

    return response;
}

export const getAzureKubernetesSecrets = async (): Promise<any[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

    const response = await fetchAzureK8ServiceSecrets(latestTenant);

    return response;
}
